import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Lightweight auth store. We persist the tokens + user identity in
 * localStorage so a browser refresh keeps the session alive until the
 * refresh token expires.
 *
 * This is intentionally thin: the API client layer (lib/apiClient.ts)
 * reads/writes through these setters, components subscribe via
 * useAuthStore(), and the ProtectedRoute guard checks isAuthenticated().
 */
export interface AuthUser {
  userId: string;
  tenantId: string;
  /** Primary role baked into the JWT at issue/refresh time. */
  role: string;
  /**
   * Every role the user holds in their current tenant. Populated by
   * the post-login `/auth/my-roles` call (see api/roleSwitch.ts).
   * Defaults to `[role]` for legacy single-role users until the fetch
   * resolves.
   */
  roles: string[];
  /**
   * Role currently active in the UI. May differ from `role` after the
   * user picks a different role from the UserMenu switcher. Used by
   * the dashboard redirect, role-scoped tour selector, and any client
   * code that wants to surface the user's _chosen_ persona. The
   * backend's permission resolver still operates on the JWT `role`
   * claim plus the union of granted permissions, so this is purely a
   * client-side UX signal.
   */
  currentRole: string;
  passwordResetRequired: boolean;
}

export interface AuthTokens {
  accessToken: string;
  accessTokenExpiresAt: string;
  /** Tessera-issued id_token. Preserved so sdk.logout() can pass it as
   *  id_token_hint and Tessera can terminate the upstream WorkOS session.
   *  Absent for local-password sessions (Kairos doesn't issue id_tokens). */
  idToken?: string;
  refreshToken: string;
  refreshTokenExpiresAt: string;
}

interface AuthState {
  user: AuthUser | null;
  tokens: AuthTokens | null;
  setSession: (user: AuthUser, tokens: AuthTokens) => void;
  setTokens: (tokens: AuthTokens) => void;
  setRoles: (roles: string[]) => void;
  setCurrentRole: (role: string) => void;
  clearPasswordResetRequired: () => void;
  clear: () => void;
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      setSession: (user, tokens) =>
        set({
          user: {
            ...user,
            // Default the roles list to the JWT role and make
            // currentRole match. Both are refined right after by
            // post-login fetches; this keeps the store internally
            // consistent during the brief in-between window.
            roles: user.roles?.length ? user.roles : [user.role],
            currentRole: user.currentRole ?? user.role,
          },
          tokens,
        }),
      setTokens: (tokens) => set({ tokens }),
      setRoles: (roles) =>
        set((s) => {
          if (!s.user) return s;
          const dedup = Array.from(new Set([s.user.role, ...roles]));
          const stillValid = dedup.includes(s.user.currentRole)
            ? s.user.currentRole
            : s.user.role;
          return { user: { ...s.user, roles: dedup, currentRole: stillValid } };
        }),
      setCurrentRole: (role) =>
        set((s) => {
          if (!s.user) return s;
          // Refuse silently if the role isn't one the user holds. The
          // backend's /auth/switch-role enforces the same; this guards
          // against a client-side bug calling setCurrentRole('FOO')
          // before fetching my-roles.
          if (!s.user.roles.includes(role)) return s;
          return { user: { ...s.user, currentRole: role } };
        }),
      clearPasswordResetRequired: () =>
        set((s) => (s.user ? { user: { ...s.user, passwordResetRequired: false } } : s)),
      clear: () => set({ user: null, tokens: null }),
      isAuthenticated: () => get().tokens !== null,
    }),
    {
      name: 'kairos.auth',
      storage: createJSONStorage(() => localStorage),
      // Backfill `roles` + `currentRole` for sessions persisted under
      // the previous schema (only `role`). Without this, a logged-in
      // user upgrading to this build would see an undefined `roles`
      // and the UserMenu would crash on `.includes()`.
      migrate: (persistedState, version) => {
        const state = persistedState as Partial<{ user: AuthUser | null }> | undefined;
        if (state?.user && !state.user.roles) {
          state.user = {
            ...state.user,
            roles: [state.user.role],
            currentRole: state.user.role,
          };
        }
        return { ...(persistedState as object), version } as unknown as AuthState;
      },
      version: 2,
    },
  ),
);
